/**
 * /ko/youtube-summary — 유튜브/영상 요약 reframe bridge (Korean).
 *
 * Targets 유튜브 영상 요약 ai (4k, KD 18) / 영상요약 ai / 유튜브 내용 요약. High
 * volume but hard KD + softer product fit; reframe toward research/talk videos.
 */
import { MarketingShell } from './MarketingShell';

export function KoYoutubeSummary() {
  return (
    <MarketingShell
      currentLocale="ko"
      hreflangPath="/youtube-summary"
      hreflangLocales={['ko']}
      documentTitle="유튜브 영상 요약 AI｜무료로 영상 내용 요약하는 법 · Draftlize"
      documentDescription="유튜브 영상을 AI로 무료 요약하는 방법. 사용자 인터뷰·발표 영상이라면, 요약을 넘어 '결정'으로 정리하는 법까지."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">가이드 · 영상 요약</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            유튜브 영상 요약 AI——<br />
            <em>그리고 제품 팀의 활용법.</em>
          </h1>
          <p className="lede">
            긴 유튜브 영상을 AI에 넣으면 몇 분 만에 핵심 요약이 나옵니다. 강의나 리뷰를 훑을 때 편리하죠. 그런데 그게 <em>사용자 인터뷰</em>나 <em>발표·데모 영상</em>이라면, 필요한 건 '짧은 요약'이 아니라 그 안에서 나온 <em>의사결정</em>입니다. 이 페이지는 유튜브·영상을 무료로 요약하는 법과, 요약을 넘어 결정으로 정리하는 법을 다룹니다.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">유튜브 영상을 무료로 요약하기.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>URL 붙여넣기</strong>——영상 요약 AI 도구에 유튜브 링크를 넣습니다.</li>
            <li><strong>요약 생성</strong>——자막·음성을 기반으로 핵심을 추립니다(대부분 무료 한도 제공).</li>
            <li><strong>확인</strong>——숫자·인용이 맞는지 검토.</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            자막만 뽑는 방법도 있지만, 자막은 '읽는 텍스트'입니다. 그리고 리서치·발표 영상이라면 한 걸음 더 있습니다. 문서 요약은 <a href="/ko/pdf-summary" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PDF 요약 가이드</a>도 참고하세요.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">요약 vs 결정</span>
            <h2 className="serif">영상 요약에서 멈추지 말고, <em>결정으로 정리하기.</em></h2>
          </div>
          <p>영상 요약 도구와 Draftlize는 하는 일이 다릅니다. 하나는 영상을 압축하고, 다른 하나는 그 안의 결정을 계속 유효하게 유지합니다.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>영상 요약 AI</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>출력</td><td className="partial">요약·핵심 정리</td><td className="ours yes">이유가 붙은 결정 카드</td></tr>
            <tr><td>리서치 영상의 가치</td><td className="no">요약에 묻힘</td><td className="ours yes">결정으로 정리됨</td></tr>
            <tr><td>몇 주 후</td><td className="no">낡았는지 모름</td><td className="ours yes">최신 또는 stale 표시</td></tr>
            <tr><td>AI 에이전트</td><td className="partial">요약 텍스트만</td><td className="ours yes">MCP로 결정을 읽음</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          영상을 완벽하게 요약해도, 그 안의 어떤 결정이 지금도 유효한지는 요약이 알려주지 않는다.
        </blockquote>
        <cite>영상 요약 도구는 그대로. 결정을 살아있게 하는 건 Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">자주 묻는 질문</span>
            <h2 className="serif">FAQ.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '무료 유튜브 요약 도구가 있나요?', a: '여러 영상 요약 AI 도구가 무료 한도를 제공합니다. Draftlize는 영상을 요약하지 않습니다——요약한 내용 속 결정을 최신으로 유지하는 카드로 만듭니다.' },
            { q: '긴 영상도 요약되나요?', a: '대부분 긴 영상도 처리합니다. 다만 요약이 길어질수록 핵심 결정이 묻히므로, 리서치·발표 영상이라면 결정으로 정리하는 단계가 중요합니다.' },
            { q: '요약을 어떻게 활용하나요?', a: '인터뷰·발표 영상의 결정을 Draftlize에 카드로 남깁니다. 다른 결정이 그것을 뒤집으면 자동으로 stale 표시되어, 낡은 결론을 알아챌 수 있습니다.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.85, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">리서치 영상의 결정을 살아있는 카드로 <em>—— $5 무료로.</em></h2>
        </div>
        <div className="trust-strip">
          <span>신규 가입 시 $5 지급</span>
          <span>사용한 만큼만 결제</span>
          <span>잔액은 만료되지 않음</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            영상 요약 도구는 그대로 쓰세요. 인터뷰·발표 영상의 결정을 Draftlize에 남기면, 이유째 최신으로 유지되고 AI 에이전트도 읽습니다.
          </p>
          <a className="btn-primary" href="/">$5 무료로 시작하기</a>
        </div>
      </section>
    </MarketingShell>
  );
}
